import { useCallback } from "react";
import { writeBatch } from "firebase/firestore";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";

type AreaSnapshot = QueryDocumentSnapshot<DocumentData>;

/**
 * Moves an area to a new parent and position, renumbering both the old and the
 * new sibling group.
 *
 * Takes the already-subscribed areas rather than subscribing again, and writes
 * everything as a single batch: a half-applied move would leave the tree in a
 * state the UI cannot repair.
 */
export const useMoveArea = (areas: AreaSnapshot[]) =>
  useCallback(
    async (areaId: string, newParentId: string | null, newIndex: number) => {
      const area = areas.find((a) => a.id === areaId);

      if (!area) {
        return;
      }

      const oldParentId = area.get("parentId") ?? null;

      const siblingsOf = (parentId: string | null) =>
        areas.filter(
          (a) => (a.get("parentId") ?? null) === parentId && a.id !== areaId
        );

      const batch = writeBatch(area.ref.firestore);

      const targetSiblings = siblingsOf(newParentId);
      targetSiblings.splice(
        Math.max(0, Math.min(newIndex, targetSiblings.length)),
        0,
        area
      );

      targetSiblings.forEach((doc, index) => {
        const order = index + 1;

        if (doc.id === areaId) {
          batch.update(doc.ref, { parentId: newParentId, order });
        } else if (parseInt(doc.get("order")) !== order) {
          batch.update(doc.ref, { order });
        }
      });

      if (oldParentId !== newParentId) {
        siblingsOf(oldParentId).forEach((doc, index) => {
          const order = index + 1;

          if (parseInt(doc.get("order")) !== order) {
            batch.update(doc.ref, { order });
          }
        });
      }

      await batch.commit();
    },
    [areas]
  );
